import type {
  QuoteInput,
  PromptOptions,
  ImagePromptObject,
  GenerationResult,
  JobMetadata,
  TypographyResult,
  ImageSize,
  ImageQuality,
} from "../types";

const API_BASE = "/api";

// Generic fetch wrapper with error handling
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }

  return data as T;
}

// Build prompts from quotes
export async function buildPrompts(
  quotes: QuoteInput[],
  options?: PromptOptions
): Promise<{ prompts: ImagePromptObject[]; count: number }> {
  return fetchApi("/prompts/build", {
    method: "POST",
    body: JSON.stringify({ quotes, options }),
  });
}

// Generate images from prompts
export async function generateImages(
  prompts: ImagePromptObject[],
  globalOptions?: { size?: ImageSize; quality?: ImageQuality }
): Promise<{
  results: GenerationResult[];
  totalRequested: number;
  successful: number;
  failed: number;
}> {
  return fetchApi("/images/generate", {
    method: "POST",
    body: JSON.stringify({ prompts, global: globalOptions }),
  });
}

// Get job by ID
export async function getJob(jobId: string): Promise<JobMetadata> {
  return fetchApi(`/jobs/${jobId}`);
}

// List recent jobs
export async function listJobs(
  limit?: number
): Promise<{ jobs: JobMetadata[]; count: number }> {
  const query = limit ? `?limit=${limit}` : "";
  return fetchApi(`/jobs${query}`);
}

// Analyze image for typography suggestions
export async function analyzeTypography(
  imageBase64: string,
  quoteText: string,
  author?: string
): Promise<TypographyResult> {
  return fetchApi("/typography/analyze", {
    method: "POST",
    body: JSON.stringify({ imageBase64, quoteText, author }),
  });
}

// Health check
export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  return fetchApi("/health");
}
